// pages/ReclamationsPage.tsx
import { useState, useMemo } from "react";
import { Search, X, Eye, CheckCircle, XCircle, Clock } from "lucide-react";
import PageLitLayout from "../../../layouts/PageListLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useReclamations, { reclamationsService } from "../../../hooks/reclamations/useReclamations";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import useToken from "../../../hooks/auth/useToken";
import ReclamationsList from "../../../components/features/reclamations/ReclamationsList";
import { Reclamation } from "../../../types/Reclamation";
import { useNavigate } from "react-router-dom";

export default function ReclamationsPage() {
  const { token } = useToken();
  const { data: reclamations, loading, refresh } = useReclamations({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatut, setSelectedStatut] = useState<string>("");
  const [selectedReclamation, setSelectedReclamation] = useState<Reclamation | null>(null);
  const [reclamationToDelete, setReclamationToDelete] = useState<Reclamation | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
 const navigate = useNavigate()
  // Statistiques
  const stats = useMemo(() => {
    const list = reclamations || [];
    return {
      total: list.length,
      soumise: list.filter(r => r.statut === "soumise").length,
      en_cours: list.filter(r => r.statut === "en_cours").length,
      resolue: list.filter(r => r.statut === "resolue").length,
      rejetee: list.filter(r => r.statut === "rejetee").length,
    };
  }, [reclamations]);

  // Filtrage
  const filteredReclamations = useMemo(() => {
    let result = reclamations || [];

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          (r.id || r._id || "").toLowerCase().includes(searchLower) ||
          (r.user?.username && r.user.username.toLowerCase().includes(searchLower)) ||
          (r.objet && r.objet.toLowerCase().includes(searchLower)) ||
          (r.reference && r.reference.toLowerCase().includes(searchLower))
      );
    }

    if (selectedStatut) {
      result = result.filter((r) => r.statut === selectedStatut);
    }

    return result;
  }, [reclamations, searchTerm, selectedStatut]);

  const handleDelete = async () => {
    if (!reclamationToDelete) return;
    setIsDeleting(true);
    try {
      const id = reclamationToDelete.id || reclamationToDelete._id || "";
      const response = await fetch(`/api/reclamations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setReclamationToDelete(null);
        alertSuccess("Réclamation supprimée");
        if (typeof refresh === "function") {
          await refresh();
        }
      }
    } catch (error) {
      console.error(error);
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    try {
      const promises = itemsToDelete.map(id =>
        fetch(`/api/reclamations/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(promises);
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} réclamations supprimées`);
      if (typeof refresh === "function") {
        await refresh();
      }
    } catch (error) {
      console.error(error);
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedStatut("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedStatut || searchTerm;

  const updateStatus = async (id: string, status: Reclamation["statut"]) => {
    try {
      await reclamationsService.updateStatut(token, id, status);
      alertSuccess(`Réclamation ${status === 'en_cours' ? 'marquée en cours' : status === 'resolue' ? 'résolue' : 'rejetée'}`);
      await refresh();
    } catch (error) {
      alertError("Une erreur s'est produite");
    }
  };

  const getActionsMenu = (reclamation: Reclamation): Menu[] => {
    const actions: Menu[] = [];

    if (reclamation.statut === "soumise") {
      actions.push({
        label: "Marquer en cours",
        icon: Clock,
        onClick: async () => {
          await updateStatus(reclamation.id, 'en_cours');
        },
      });
      actions.push({
        label: "Rejeter",
        icon: XCircle,
        onClick: async () => {
          await updateStatus(reclamation.id, 'rejetee');
        },
      });
    }

    if (reclamation.statut === "en_cours") {
      actions.push({
        label: "Marquer résolue",
        icon: CheckCircle,
        onClick: async () => {
          await updateStatus(reclamation.id, 'resolue');
        },
      });
      actions.push({
        label: "Rejeter",
        icon: XCircle,
        onClick: async () => {
          await updateStatus(reclamation.id, 'rejetee');
        },
      });
    }

    if (reclamation.statut === "resolue") {
      actions.push({
        label: "Marquer en cours",
        icon: Clock,
        onClick: async () => {
          await updateStatus(reclamation.id, 'en_cours');
        },
      });
    }

    if (reclamation.statut === "rejetee") {
      actions.push({
        label: "Marquer en cours",
        icon: Clock,
        onClick: async () => {
          await updateStatus(reclamation.id, 'en_cours');
        },
      });
    }

    actions.push({
      label: "Voir détails",
      icon: Eye,
      onClick: () => {
        navigate(`/admin/reclamations/details/${reclamation.id}`)
      },
    });

    actions.push({
      label: "Supprimer",
      icon: X,
      onClick: () => setReclamationToDelete(reclamation),
    });

    return actions;
  };

  return (
    <PageLitLayout title="">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Réclamations</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez toutes les réclamations clients</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
              <p className="text-xs text-yellow-700/70">Soumise</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.soumise}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
              <p className="text-xs text-purple-700/70">En cours</p>
              <p className="text-2xl font-bold text-purple-700">{stats.en_cours}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Résolue</p>
              <p className="text-2xl font-bold text-green-700">{stats.resolue}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
              <p className="text-xs text-red-700/70">Rejetée</p>
              <p className="text-2xl font-bold text-red-700">{stats.rejetee}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une réclamation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4 bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-700">Filtres</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <X size={14} /> Effacer tout
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={selectedStatut}
                  onChange={(e) => setSelectedStatut(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Tous</option>
                  <option value="soumise">Soumise</option>
                  <option value="en_cours">En cours</option>
                  <option value="resolue">Résolue</option>
                  <option value="rejetee">Rejetée</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {filteredReclamations.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucune réclamation trouvée</p>
              </div>
            )}
            <ReclamationsList
              reclamations={filteredReclamations}
              onAction={(reclamation) => setSelectedReclamation(reclamation)}
              onSelectReclamations={(selected) => setItemsToDelete(selected.map((r) => r.id || r._id || ""))}
              selectable={false}
              selectActions={[
                {
                  label: "Supprimer",
                  onClick: (selected) => setItemsToDelete(selected.map((r) => r.id || r._id || "")),
                  className: "bg-red-600 text-white",
                },
              ]}
            />
          </>
        )}
      </div>

      {/* Menu modal */}
      {selectedReclamation && (
        <MenuModal
          title={`Gérer la Réclamation`}
          isOpen={!!selectedReclamation}
          onClose={() => setSelectedReclamation(null)}
          icon={null}
          menu={getActionsMenu(selectedReclamation)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!reclamationToDelete}
        onClose={() => setReclamationToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la réclamation"
        message={`Supprimer la réclamation "${reclamationToDelete?.objet}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} réclamations ?`}
      />
    </PageLitLayout>
  );
}